import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import {
  type InstagramProfilePlannerSnapshot,
  instagramProfilePlannerService,
} from '@/services/data/InstagramProfilePlannerService';

export const instagramProfilePlannerKey = (connectionId: string | undefined) =>
  ['instagram-profile-planner', connectionId ?? null] as const;

const EMPTY_SNAPSHOT = (connectionId: string): InstagramProfilePlannerSnapshot => ({
  planId: null,
  connectionId,
  version: 0,
  updatedAt: null,
  items: [],
});

export function useInstagramProfilePlanner(connectionId: string | undefined) {
  const queryClient = useQueryClient();
  const conversionOperations = useRef(new Map<string, string>());
  const key = instagramProfilePlannerKey(connectionId);
  const query = useQuery({
    queryKey: key,
    enabled: Boolean(connectionId),
    queryFn: async () => {
      const result = await instagramProfilePlannerService.getPlan(connectionId!);
      if (result.error) throw result.error;
      return result.data ?? EMPTY_SNAPSHOT(connectionId!);
    },
    staleTime: 15_000,
  });

  const addCandidates = useMutation({
    mutationFn: async (input: { mediaIds: string[]; projectId?: string | null }) => {
      if (!connectionId) throw new Error('Choose an Instagram account first');
      const current = queryClient.getQueryData<InstagramProfilePlannerSnapshot>(key);
      const result = await instagramProfilePlannerService.addCandidates({
        connectionId,
        mediaIds: input.mediaIds,
        expectedVersion: current?.planId ? current.version : null,
        projectId: input.projectId,
      });
      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const savePositions = useMutation({
    mutationFn: async (orderedItemIds: string[]) => {
      const current = queryClient.getQueryData<InstagramProfilePlannerSnapshot>(key);
      if (!current?.planId) throw new Error('Add media before arranging this plan');
      const result = await instagramProfilePlannerService.savePositions({
        planId: current.planId,
        expectedVersion: current.version,
        orderedItemIds,
      });
      if (result.error) throw result.error;
      return result.data;
    },
    onMutate: async (orderedItemIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<InstagramProfilePlannerSnapshot>(key);
      if (previous) {
        const byId = new Map(previous.items.map((item) => [item.id, item]));
        queryClient.setQueryData<InstagramProfilePlannerSnapshot>(key, {
          ...previous,
          items: orderedItemIds.flatMap((id, index) => {
            const item = byId.get(id);
            return item ? [{ ...item, position: index + 1 }] : [];
          }),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (result) => {
      if (!result) return;
      queryClient.setQueryData<InstagramProfilePlannerSnapshot>(key, (current) =>
        current
          ? { ...current, version: result.version, updatedAt: new Date().toISOString() }
          : current,
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const removeCandidate = useMutation({
    mutationFn: async (itemId: string) => {
      const current = queryClient.getQueryData<InstagramProfilePlannerSnapshot>(key);
      if (!current?.planId) throw new Error('Instagram plan unavailable');
      const result = await instagramProfilePlannerService.removeCandidate({
        planId: current.planId,
        itemId,
        expectedVersion: current.version,
      });
      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const createPosts = useMutation({
    mutationFn: async (itemIds: string[]) => {
      const current = queryClient.getQueryData<InstagramProfilePlannerSnapshot>(key);
      if (!current?.planId) throw new Error('Instagram plan unavailable');
      const operationKey = [...new Set(itemIds)].sort().join(',');
      const operationId = conversionOperations.current.get(operationKey) ?? crypto.randomUUID();
      conversionOperations.current.set(operationKey, operationId);
      const result = await instagramProfilePlannerService.createPosts({
        planId: current.planId,
        itemIds,
        expectedVersion: current.version,
        operationId,
      });
      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: async (_result, itemIds) => {
      conversionOperations.current.delete([...new Set(itemIds)].sort().join(','));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: ['instagram-feed-plan', connectionId] }),
        queryClient.invalidateQueries({ queryKey: ['calendar-posts'] }),
      ]);
    },
  });

  return {
    snapshot: query.data ?? EMPTY_SNAPSHOT(connectionId ?? ''),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    addCandidates,
    savePositions,
    removeCandidate,
    createPosts,
  };
}

export function useInstagramPlannerTarget(targetId: string | undefined) {
  return useQuery({
    queryKey: ['instagram-profile-planner-target', targetId ?? null],
    enabled: Boolean(targetId),
    queryFn: async () => {
      const result = await instagramProfilePlannerService.getTarget(targetId!);
      if (result.error) throw result.error;
      return result.data ?? null;
    },
    staleTime: 15_000,
  });
}
